import { Siren, ScanEye, HardHat, Droplets, FlaskConical, FileText, QrCode, BadgeCheck, LayoutGrid } from "lucide-react";

export const TOOLS = [
  {
    id: "first-aid",
    label: "Emergency & First-Aid",
    short: "First-Aid",
    icon: Siren,
    accent: "#c1442d",
    desc: "Chemical + route-specific steps, plus a full-screen Emergency Mode with a live flush timer.",
  },
  {
    id: "ghs",
    label: "Label Reader",
    short: "Labels",
    icon: ScanEye,
    accent: "#4A5C6E",
    desc: "Decode GHS pictograms & NFPA diamond, or check an unknown label to identify which of the four it is.",
  },
  {
    id: "ppe",
    label: "PPE Kit Builder",
    short: "PPE",
    icon: HardHat,
    accent: "#e8a33d",
    desc: "Task + duration + concentration → printable pre-entry checklist with supervisor sign-off.",
  },
  {
    id: "spill",
    label: "Spill Response Planner",
    short: "Spill",
    icon: Droplets,
    accent: "#4A5C6E",
    desc: "Enter volume in litres → auto-classifies, sizes containment radius, and logs the incident.",
  },
  {
    id: "compat",
    label: "Compatibility What-if",
    short: "What-if",
    icon: FlaskConical,
    accent: "#1f8a70",
    desc: "Pick material + chemical + operating temperature → instant verdict with reasoning and alternates.",
  },
  {
    id: "sds",
    label: "SDS Library",
    short: "SDS",
    icon: FileText,
    accent: "#4A5C6E",
    desc: "Current-revision Safety Data Sheets for every grade, in English & Hindi.",
  },
  {
    id: "coa",
    label: "COA Verifier",
    short: "COA",
    icon: QrCode,
    accent: "#D63031",
    desc: "Look up a batch, get a scannable QR verification code, and see the batch history for that grade.",
  },
  {
    id: "standards",
    label: "BIS / IS / FSSAI",
    short: "Standards",
    icon: BadgeCheck,
    accent: "#1f6a4f",
    desc: "Plain-language index of every Indian standard each grade is manufactured and tested against.",
  },
];

export default function ToolPicker({ activeTool, onSelect, activeChem, activeChemName }) {
  return (
    <section className="tool-picker" data-testid="tool-picker" id="tool-picker-anchor">
      <div className="wrap">
        <div className="tool-picker-head">
          <div className="eyebrow">The toolbelt</div>
          <h2>Eight tools, one active chemical</h2>
          <p>
            Pick a tool below to focus on it — everything is filtered to{" "}
            <b>{activeChemName || "the active substance"}</b> from the strip above. Switch either
            filter any time.
          </p>
        </div>

        <div className="tool-filter-row" role="tablist" data-testid="tool-filter-row">
          <button
            role="tab"
            aria-selected={!activeTool}
            className={"tool-filter-chip" + (!activeTool ? " on" : "")}
            onClick={() => onSelect(null)}
            data-testid="tool-filter-all"
          >
            <LayoutGrid size={13} />
            All tools
          </button>
          {TOOLS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeTool === t.id}
              className={"tool-filter-chip" + (activeTool === t.id ? " on" : "")}
              onClick={() => onSelect(t.id)}
              data-testid={`tool-filter-${t.id}`}
            >
              <t.icon size={13} />
              {t.short}
            </button>
          ))}
        </div>

        <div className="tool-card-grid" data-testid="tool-card-grid">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const on = activeTool === t.id;
            return (
              <button
                key={t.id}
                type="button"
                className={"tool-card" + (on ? " on" : "")}
                onClick={() => {
                  onSelect(t.id);
                  // Small delay so the section renders before we scroll
                  setTimeout(() => {
                    document.getElementById(t.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 30);
                }}
                data-testid={`tool-card-${t.id}`}
              >
                <div className="tool-card-icon" style={{ background: t.accent }}>
                  <Icon size={20} strokeWidth={1.9} />
                </div>
                <div className="tool-card-body">
                  <div className="tool-card-name">{t.label}</div>
                  <div className="tool-card-desc">{t.desc}</div>
                </div>
                <span className="tool-card-arrow" aria-hidden>→</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
